import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { companyApi } from '../lib/api';
import AIAssistantBubble from './AIAssistantBubble';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Truck, 
  LayoutDashboard, 
  Users, 
  Package, 
  Calendar, 
  FileText, 
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  DollarSign,
  History,
  Building2,
  Moon,
  Sun,
  Wrench,
  FolderOpen,
  RefreshCw,
  UserCheck,
  Headphones,
  Receipt,
  BarChart3,
  ShieldCheck,
  ClipboardList,
  Briefcase,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { cn } from '../lib/utils';

export default function Layout({ children }) {
  const { user, logout, isAdmin, isDriver, canViewWeeklySummary, canViewDriverFiles } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Desktop collapse (persisted).  When true, sidebar hides but a hover-zone
  // on the far left of the screen reveals it temporarily.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar_collapsed') === '1'; }
    catch { return false; }
  });
  const [sidebarHoverPeek, setSidebarHoverPeekRaw] = useState(false);
  const peekTimerRef = React.useRef(null);
  // Debounced setter — opens instantly, closes after a 180ms grace window so
  // brief mouse-outs (e.g. moving across a submenu boundary) don't flicker.
  const setSidebarHoverPeek = React.useCallback((v) => {
    if (peekTimerRef.current) { clearTimeout(peekTimerRef.current); peekTimerRef.current = null; }
    if (v) {
      setSidebarHoverPeekRaw(true);
    } else {
      peekTimerRef.current = setTimeout(() => setSidebarHoverPeekRaw(false), 180);
    }
  }, []);
  useEffect(() => {
    try { localStorage.setItem('sidebar_collapsed', sidebarCollapsed ? '1' : '0'); }
    catch { /* ignore */ }
  }, [sidebarCollapsed]);
  const [company, setCompany] = useState(null);

  // Onboarding tour asks us to keep the sidebar open while it runs so the
  // tour can highlight nav items on mobile / narrow screens.
  useEffect(() => {
    const open = () => setSidebarOpen(true);
    const close = () => setSidebarOpen(false);
    window.addEventListener('tour:open-sidebar', open);
    window.addEventListener('tour:close-sidebar', close);
    return () => {
      window.removeEventListener('tour:open-sidebar', open);
      window.removeEventListener('tour:close-sidebar', close);
    };
  }, []);

  // Build navigation based on user role and permissions
  const getNavigation = () => {
    if (isDriver) {
      // Driver-specific navigation - only show permitted items
      const driverNav = [
        { name: 'Dashboard', href: '/', icon: LayoutDashboard },
      ];
      
      if (canViewWeeklySummary) {
        driverNav.push({ name: 'Summary', href: '/summary', icon: FileText });
      }
      
      if (canViewDriverFiles) {
        driverNav.push({ name: 'Driver Files', href: '/driver-files', icon: FolderOpen });
      }
      
      return driverNav;
    }
    
    // Full navigation for non-driver users
    return [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard },
      { name: 'Summary', href: '/summary', icon: FileText },
      { name: 'Loads', href: '/loads', icon: Package },
      { name: 'Deductions', href: '/deductions', icon: DollarSign, featureKey: 'deductions' },
      { name: 'Drivers', href: '/drivers', icon: Users },
      { name: 'Units', href: '/units', icon: Truck },
      {
        name: 'People',
        icon: Users,
        children: [
          { name: 'Staff', href: '/staff', icon: Briefcase },
          { name: 'Owner Operators', href: '/owner-operators', icon: UserCheck, featureKey: 'owner_operators' },
          { name: 'Dispatchers', href: '/dispatchers', icon: Headphones },
          { name: 'Paystubs', href: '/paystubs', icon: Receipt },
        ],
      },
      { name: 'Schedule', href: '/schedule', icon: Calendar, featureKey: 'schedule' },
      { name: 'History', href: '/history', icon: History, featureKey: 'history' },
      { name: 'Vehicle Mgmt', href: '/vehicle-management', icon: Wrench, featureKey: 'vehicle_management' },
      { name: 'Driver Files', href: '/driver-files', icon: FolderOpen, featureKey: 'driver_files' },
      { name: 'Accounting', href: '/accounting', icon: BarChart3, featureKey: 'accounting' },
    ];
  };

  // Filter navigation based on company feature toggles and user feature access.
  // Group items keep their children only if at least one passes the filter.
  const companyToggles = company?.feature_toggles || {};
  const userAccess = user?.feature_access || {};
  const isAllowed = (item) => {
    if (!item.featureKey) return true;
    if (companyToggles[item.featureKey] === false) return false;
    if (userAccess[item.featureKey] === false) return false;
    return true;
  };
  const rawNav = getNavigation();
  const navigation = rawNav
    .map(item => {
      if (!item.children) return item;
      const kept = item.children.filter(isAllowed);
      return kept.length ? { ...item, children: kept } : null;
    })
    .filter(item => item && isAllowed(item));

  const adminNavigation = [
    { name: 'Company Settings', href: '/company', icon: Building2 },
  ];

  useEffect(() => {
    loadCompany();
  }, []);

  const loadCompany = async () => {
    try {
      const res = await companyApi.get();
      setCompany(res.data);
    } catch (error) {
      // Ignore error, use default
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const [expandedGroups, setExpandedGroups] = useState(() => {
    // Auto-expand the group whose child path matches the current location
    const current = {};
    return current;
  });

  const toggleGroup = (name) => {
    setExpandedGroups(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const NavLink = ({ item }) => {
    if (item.children) {
      // Group with sub-items — auto-expand if a child route is active
      const childActive = item.children.some(c => location.pathname === c.href);
      const expanded = childActive || !!expandedGroups[item.name];
      return (
        <div>
          <button
            type="button"
            onClick={() => toggleGroup(item.name)}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200',
              childActive
                ? 'text-white bg-slate-800'
                : 'text-slate-300 hover:bg-slate-800 hover:text-white'
            )}
            data-testid={`nav-group-${item.name.toLowerCase().replace(' ', '-')}`}
          >
            <item.icon className="h-5 w-5" />
            <span className="font-medium flex-1 text-left">{item.name}</span>
            <ChevronRight className={cn('h-4 w-4 transition-transform', expanded && 'rotate-90')} />
          </button>
          {expanded && (
            <div className="ml-3 mt-1 space-y-1 border-l border-slate-700 pl-2">
              {item.children.map(child => {
                const isActive = location.pathname === child.href;
                return (
                  <Link
                    key={child.name}
                    to={child.href}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-all',
                      isActive
                        ? 'bg-blue-600 text-white shadow'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    )}
                    data-testid={`nav-${child.name.toLowerCase().replace(' ', '-')}`}
                  >
                    <child.icon className="h-4 w-4" />
                    <span>{child.name}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    const isActive = location.pathname === item.href;
    return (
      <Link
        to={item.href}
        onClick={() => setSidebarOpen(false)}
        className={cn(
          'flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200',
          isActive 
            ? 'bg-blue-600 text-white shadow-md' 
            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
        )}
        data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
      >
        <item.icon className="h-5 w-5" />
        <span className="font-medium">{item.name}</span>
        {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
      </Link>
    );
  };

  // Detect impersonation session — set by SuperAdmin "Login as User".
  const impersonating = (() => {
    try {
      const raw = localStorage.getItem('impersonating');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  const exitImpersonation = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('impersonating');
    // SuperAdmin password screen lives at /super-admin; it persists its own auth state in memory only,
    // so we can safely send the user back there to pick another account.
    window.location.replace('/super-admin');
  };

  return (
    <div className="min-h-screen bg-background app-shell">
      {/* Impersonation banner — sticky top, only when super-admin is viewing as another user */}
      {impersonating && (
        <div
          className="sticky top-0 z-[60] bg-amber-500 text-amber-950 px-4 py-2 text-sm font-medium flex items-center justify-between gap-4 shadow-md"
          data-testid="impersonation-banner"
        >
          <span className="flex items-center gap-2 truncate">
            <span className="inline-block h-2 w-2 bg-amber-900 rounded-full animate-pulse" />
            <span className="truncate">Viewing as <b>{impersonating.user_name || impersonating.user_email}</b> — actions affect this account.</span>
          </span>
          <Button
            size="sm"
            variant="outline"
            className="bg-white/30 hover:bg-white/50 border-amber-900/40 text-amber-950 font-semibold flex-shrink-0"
            onClick={exitImpersonation}
            data-testid="exit-impersonation-btn"
          >
            Return to Admin
          </Button>
        </div>
      )}
      {/* AI Assistant floating chat — DISABLED (dormant). Kept for future reactivation. */}
      {/* <AIAssistantBubble /> */}
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Desktop: visible discoverability rail + hover-peek zone.
          The rail is a slim gradient strip anchored to the far-left of the
          viewport; it gives users a clear signal that a hidden sidebar exists
          and is much easier to hit than a 2px transparent zone. Hovering the
          rail OR the wider 12px zone opens the sidebar smoothly. Once inside,
          hovering the sidebar itself keeps it open — leaving both closes it
          (100 ms hysteresis prevents flicker). */}
      {sidebarCollapsed && (
        <>
          <button
            type="button"
            className="hidden lg:flex fixed left-0 top-0 h-full w-2 z-40 items-center justify-center bg-gradient-to-r from-slate-900/80 to-slate-900/0 hover:from-blue-600/90 group cursor-pointer transition-colors"
            onMouseEnter={() => setSidebarHoverPeek(true)}
            onClick={() => setSidebarCollapsed(false)}
            aria-label="Open navigation"
            data-testid="sidebar-hover-zone"
            title="Open navigation"
          >
            <span className="absolute left-0.5 top-1/2 -translate-y-1/2 flex flex-col items-center gap-0.5 opacity-70 group-hover:opacity-100 transition-opacity">
              <span className="block w-0.5 h-4 rounded-full bg-white/60" />
              <span className="block w-0.5 h-4 rounded-full bg-white/60" />
              <span className="block w-0.5 h-4 rounded-full bg-white/60" />
            </span>
          </button>
          {/* Wider transparent catch-zone for easier hover activation */}
          <div
            className="hidden lg:block fixed left-0 top-0 h-full w-3 z-40 pointer-events-auto"
            onMouseEnter={() => setSidebarHoverPeek(true)}
            data-testid="sidebar-hover-catch"
          />
        </>
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-full w-64 bg-slate-900 transform transition-transform duration-300 ease-in-out',
          // Mobile behavior (unchanged): controlled by sidebarOpen
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop behavior: visible unless collapsed; peek on hover-zone
          sidebarCollapsed
            ? (sidebarHoverPeek ? 'lg:translate-x-0 lg:shadow-2xl' : 'lg:-translate-x-full')
            : 'lg:translate-x-0',
        )}
        onMouseEnter={() => sidebarCollapsed && setSidebarHoverPeek(true)}
        onMouseLeave={() => setSidebarHoverPeek(false)}
        data-testid="sidebar-aside"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-slate-800">
            <div className="flex items-center gap-3">
              {company?.logo_url ? (
                <img 
                  src={company.logo_url} 
                  alt="Company Logo" 
                  className="h-10 w-10 object-contain rounded-lg bg-white p-1"
                />
              ) : (
                <div className="p-2 bg-blue-600 rounded-lg">
                  <Truck className="h-6 w-6 text-white" />
                </div>
              )}
              <span className="text-lg font-manrope font-bold text-white truncate max-w-[140px]">
                {company?.name || 'My Dispatch'}
              </span>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden text-slate-400 hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="hidden lg:inline-flex text-slate-400 hover:text-white"
              onClick={() => { setSidebarCollapsed(true); setSidebarHoverPeek(false); }}
              title="Collapse sidebar"
              data-testid="sidebar-collapse-btn"
            >
              <PanelLeftClose className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navigation.map((item) => (
              <NavLink key={item.name} item={item} />
            ))}
            
            {isAdmin && (
              <>
                <div className="pt-4 pb-2">
                  <span className="px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Admin
                  </span>
                </div>
                {adminNavigation.map((item) => (
                  <NavLink key={item.name} item={item} />
                ))}
              </>
            )}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10 bg-blue-600">
                <AvatarFallback className="bg-blue-600 text-white font-medium">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={cn('transition-all duration-300', sidebarCollapsed ? 'lg:pl-0' : 'lg:pl-64')}>
        {/* Top header */}
        <header className="sticky top-0 z-30 app-header backdrop-blur-md">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="icon" 
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-6 w-6" />
              </Button>
              {sidebarCollapsed && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="hidden lg:inline-flex"
                  onClick={() => setSidebarCollapsed(false)}
                  title="Expand sidebar"
                  data-testid="sidebar-expand-btn"
                >
                  <PanelLeftOpen className="h-5 w-5" />
                </Button>
              )}
            </div>
            
            <div className="flex-1 lg:flex-none" />

            <div className="flex items-center gap-2">
              {/* Dark Mode Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="h-9 w-9"
                data-testid="theme-toggle-btn"
              >
                {isDark ? (
                  <Sun className="h-5 w-5 text-yellow-500" />
                ) : (
                  <Moon className="h-5 w-5 text-slate-600" />
                )}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    <Avatar className="h-8 w-8 bg-blue-600">
                      <AvatarFallback className="bg-blue-600 text-white text-sm">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline font-medium">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div>
                      <p className="font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={toggleTheme}
                    className="cursor-pointer"
                    data-testid="theme-toggle-menu"
                  >
                    {isDark ? (
                      <>
                        <Sun className="h-4 w-4 mr-2" />
                        Light Mode
                      </>
                    ) : (
                      <>
                        <Moon className="h-4 w-4 mr-2" />
                        Dark Mode
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-red-600 cursor-pointer"
                    data-testid="logout-btn"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
